// src/stores/themeStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Define DEFAULT theme (your original app colors)
export const DEFAULT_THEME = {
  id: 'default',
  name: 'Default',
  icon: '',
  colors: {
    primary: '#254b88',//Your original blue
    secondary: '#10B981',    // Your original teal
    accent: '#8B5CF6',       // Your original purple
    background: '#F8FAFC',   // Your original light background
    foreground: '#1E293B',   // Your original dark text
    muted: '#F1F5F9',        // Your original muted
    card: '#FFFFFF',         // White cards
    border: '#E2E8F0',       // Your original border
  }
} as const;

// Define all hero color palettes
export const HERO_THEMES = {
  default: DEFAULT_THEME,  // Add default as first option
  superman: {
    id: 'superman',
    name: 'Superman',
    icon: '',
    colors: {
      primary: '#DC143C',     // Crimson red
      secondary: '#0D2F8F',   // Superman blue
      accent: '#FFD700',      // Gold
      background: '#FFFFFF',
      foreground: '#000000',
      muted: '#F5F5F5',
      card: '#FFFFFF',
      border: '#E5E5E5',
    }
  },
  flash: {
    id: 'flash',
    name: 'The Flash',
    icon: '',
    colors: {
      primary: '#FFD700',     // Gold
      secondary: '#DC143C',   // Red
      accent: '#FFFFFF',
      background: '#0A0A0A',
      foreground: '#FFFFFF',
      muted: '#1A1A1A',
      card: '#1A1A1A',
      border: '#333333',
    }
  },
  batman: {
    id: 'batman',
    name: 'Batman',
    icon: '',
    colors: {
      primary: '#000000',     // Black
      secondary: '#FFD700',   // Yellow
      accent: '#7B7B7B',
      background: '#0F0F0F',
      foreground: '#FFFFFF',
      muted: '#1A1A1A',
      card: '#1A1A1A',
      border: '#333333',
    }
  },
  hulk: {
    id: 'hulk',
    name: 'Hulk',
    icon: '',
    colors: {
      primary: '#228B22',     // Forest green
      secondary: '#4B0082',   // Indigo
      accent: '#98FF98',
      background: '#0A0A0A',
      foreground: '#FFFFFF',
      muted: '#1A1A1A',
      card: '#1A1A1A',
      border: '#228B22',
    }
  },
  catwoman: {
    id: 'catwoman',
    name: 'Catwoman',
    icon: '',
    colors: {
      primary: '#000000',     // Black
      secondary: '#9370DB',   // Medium purple
      accent: '#D8BFD8',
      background: '#1A1A1A',
      foreground: '#FFFFFF',
      muted: '#2A2A2A',
      card: '#2A2A2A',
      border: '#9370DB',
    }
  },
  riddler: {
    id: 'riddler',
    name: 'The Riddler',
    icon: '',
    colors: {
      primary: '#9370DB',     // Medium purple
      secondary: '#228B22',   // Forest green
      accent: '#98FF98',
      background: '#0F0F0F',
      foreground: '#FFFFFF',
      muted: '#1A1A1A',
      card: '#1A1A1A',
      border: '#9370DB',
    }
  }
} as const;

export type HeroId = keyof typeof HERO_THEMES;
export type HeroTheme = typeof HERO_THEMES[HeroId];

interface ThemeStore {
  // State
  selectedHero: HeroId;
  isSelectorOpen: boolean;
  
  // Actions
  selectHero: (heroId: HeroId) => void;
  resetToDefault: () => void;
  toggleSelector: () => void;
  openSelector: () => void;
  closeSelector: () => void;
  
  // Getters
  getCurrentTheme: () => HeroTheme;
  getAllHeroes: () => HeroTheme[];
  getDefaultTheme: () => HeroTheme;
  
  // Apply theme to CSS variables
  applyTheme: (heroId?: HeroId) => void;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      // Initial state - default to original app theme
      selectedHero: 'default',
      isSelectorOpen: false,
      
      // Select hero
      selectHero: (heroId: HeroId) => {
        set({ selectedHero: heroId });
        get().applyTheme(heroId);
      },
      
      // Reset to default theme
      resetToDefault: () => {
        set({ selectedHero: 'default' });
        get().applyTheme('default');
      },
      
      // Toggle selector panel
      toggleSelector: () => {
        set((state) => ({ isSelectorOpen: !state.isSelectorOpen }));
      },
      
      openSelector: () => {
        set({ isSelectorOpen: true });
      },
      
      closeSelector: () => {
        set({ isSelectorOpen: false });
      },
      
      // Get current theme
      getCurrentTheme: () => {
        return HERO_THEMES[get().selectedHero];
      },
      
      // Get all heroes (including default)
      getAllHeroes: () => {
        return Object.values(HERO_THEMES);
      },
      
      // Get default theme
      getDefaultTheme: () => {
        return DEFAULT_THEME;
      },
      
      // Apply theme to CSS variables
      applyTheme: (heroId?: HeroId) => {
        const theme = HERO_THEMES[heroId || get().selectedHero];
        const colors = theme.colors;
        
        // Apply CSS variables to root
        const root = document.documentElement;
        
        root.style.setProperty('--theme-primary', colors.primary);
        root.style.setProperty('--theme-secondary', colors.secondary);
        root.style.setProperty('--theme-accent', colors.accent);
        root.style.setProperty('--theme-background', colors.background);
        root.style.setProperty('--theme-foreground', colors.foreground);
        root.style.setProperty('--theme-muted', colors.muted);
        root.style.setProperty('--theme-card', colors.card);
        root.style.setProperty('--theme-border', colors.border);
        
        // Update HSL values for Tailwind
        const primaryHsl = hexToHsl(colors.primary);
        const secondaryHsl = hexToHsl(colors.secondary);
        
        root.style.setProperty('--primary', `${primaryHsl.h} ${primaryHsl.s}% ${primaryHsl.l}%`);
        root.style.setProperty('--secondary', `${secondaryHsl.h} ${secondaryHsl.s}% ${secondaryHsl.l}%`);
        root.style.setProperty('--background', hexToHslString(colors.background));
        root.style.setProperty('--foreground', hexToHslString(colors.foreground));
        
        // Add hero-specific class
        root.setAttribute('data-hero', theme.id);
      }
    }),
    {
      name: 'hero-theme-storage',
      onRehydrateStorage: () => (state) => {
        // Apply theme when store rehydrates from localStorage
        if (state?.selectedHero) {
          setTimeout(() => state.applyTheme(state.selectedHero), 0);
        }
      }
    }
  )
);

// Helper functions for color conversion
function hexToHsl(hex: string) {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    
    h /= 6;
  }
  
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

function hexToHslString(hex: string) {
  const { h, s, l } = hexToHsl(hex);
  return `${h} ${s}% ${l}%`;
}

// Convenience hooks
export const useSelectedHero = () => useThemeStore((state) => state.selectedHero);
export const useCurrentTheme = () => useThemeStore((state) => state.getCurrentTheme());
export const useThemeActions = () => useThemeStore((state) => ({
  selectHero: state.selectHero,
  resetToDefault: state.resetToDefault,
  toggleSelector: state.toggleSelector,
  openSelector: state.openSelector,
  closeSelector: state.closeSelector,
}));